"use client";

import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Pencil } from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

import SocialMediaForm from "../molecules/SocialMediaForm";
import OfficeAddressForm from "../molecules/OfficeAddressForm";
import AdminModal from "../../components/modals/AdminModal";
import FilePondUploader from "../molecules/FilePondUploader";
import { TagSelectInput } from "../molecules/TagSelectInput";

import { useCompanyProfileStore } from "../../store/useCompanyProfileStore";
import { useJobFilterStore } from "../../store/useJobFilterStore";

import { FileWithPreview } from "../molecules/FilePondUploader";

import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";

type Props = {
  profile: {
    gallery_image?: string[];
  };
  openModal: (type: string) => void;
};

export default function AdminProfileContent() {
  const {
    profile,
    fetchCompanyProfile,
    fetchCompanyProfileByLang,
    updateAboutUs,
    updateCulture,
    updateBenefits,
    updateAddress,
    updateGalleryImages
  } = useCompanyProfileStore();

  const updateSocialMedia = useCompanyProfileStore(
    (state) => state.updateSocialMedia
  );



  const location = useLocation();
  const lang = location.pathname.startsWith("/id") ? "id" : "en";

  const { benefitSet, fetchFilters } = useJobFilterStore();
  const benefitOptions = benefitSet?.map((item) => ({
    id: item.id,
    name: item.name
  })) ?? [];

  useEffect(() => {
    fetchFilters(lang);
  }, [lang, fetchFilters]);

  const [modalType, setModalType] = useState<string | null>(null);
  const [draftAboutID, setDraftAboutID] = useState("");
  const [draftAboutEN, setDraftAboutEN] = useState("");
  const [draftCultureID, setDraftCultureID] = useState("");
  const [draftCultureEN, setDraftCultureEN] = useState("");
  const [benefits, setBenefits] = useState<string[]>([]);
  const [officeData, setOfficeData] = useState({ address: "", map: "", isValid: false });
  const [socialMedia, setSocialMedia] = useState({
    instagram: "",
    tiktok: "",
    facebook: "",
    linkedin: "",
  });
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [galleryFiles, setGalleryFiles] = useState<FileWithPreview[]>(
    profile?.gallery_images?.map(url => ({ file: null, preview: url, isNew: false })) || []
  );

  const getBenefitLabel = (id: string | number) => {
    return benefitOptions.find(
      (opt) => String(opt.id) === String(id)
    )?.name || id;
  };


  useEffect(() => {
    fetchCompanyProfile();
  }, []);

  // Sync to UI
  useEffect(() => {
    if (!profile) return;

    setBenefits(profile.benefits || []);
    setSocialMedia({
      instagram: profile.instagram || "",
      tiktok: profile.tiktok || "",
      facebook: profile.facebook || "",
      linkedin: profile.linkedin || "",
    });
  }, [profile]);

  const openModal = async (type: string) => {
    setModalType(type);

    if (type === "about") {

      setDraftAboutID(profile?.about_us.id || "");
      setDraftAboutEN(profile?.about_us.en || "");
    }

    if (type === "Culture") {

      setDraftCultureID(profile?.culture.id || "");
      setDraftCultureEN(profile?.culture.en || "");
    }

    if (type === "gallery") {
      // Ambil file lama dari profile
      const oldFiles = profile?.gallery_images.map(url => ({ file: null, preview: url, isNew: false })) || [];
      // Gabungkan dengan file baru yang sudah ada di state
      setGalleryFiles(prevFiles => {
        // prevFiles bisa berisi file baru yang belum di-upload
        const newFiles = prevFiles.filter(f => f.isNew); // hanya file baru
        return [...oldFiles, ...newFiles];
      });
    }
  };

  const getLangValue = (
    value?: { id?: string; en?: string },
    lang: "id" | "en" = "en"
  ) => {
    return value?.[lang] || "";
  };

  const closeModal = () => setModalType(null);

  const decodeHtml = (htmlString: string) => {
    const txt = document.createElement("textarea");
    txt.innerHTML = htmlString;
    return txt.value;
  };

  function extractIframeSrc(htmlString: string): string | null {
    const match = htmlString.match(/src="([^"]+)"/);
    return match ? match[1] : null;
  }

  const isAboutValid =
    draftAboutID.trim().length > 0 &&
    draftAboutEN.trim().length > 0;

  const isCultureValid =
    draftCultureID.trim().length > 0 &&
    draftCultureEN.trim().length > 0;

  const isOfficeValid =
    typeof officeData?.address === "string" &&
    officeData.address.trim().length > 0;

  return (
    <>
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">

        <div className="lg:col-span-2 space-y-6">

          <div className="bg-white p-6 rounded-xl shadow-sm">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">{lang === "id" ? "Tentang Kami" : "About Us"}<span className="text-red-500">*</span></h3>
              <button
                onClick={() => openModal("about")}
                className="flex items-center gap-1 text-primary"
              >
                <Pencil size={16} /> Edit
              </button>
            </div>
            <p className="mt-2 text-gray-700">
              {getLangValue(profile?.about_us, lang) || "No about description yet."}
            </p>
          </div>

          {/* GALLERY */}
          <div className="bg-white p-6 rounded-xl shadow-sm">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">{lang === "id" ? "Galeri" : "Gallery"}</h3>
              <button
                onClick={() => openModal("gallery")}
                className="flex items-center gap-1 text-primary"
              >
                <Pencil size={16} />{lang === "id" ? "Tambah Foto" : "Add More Photo"}
              </button>
            </div>

            {profile?.gallery_images?.length ? (
              <div className="mt-4">
                <Swiper
                  modules={[Pagination, Navigation]}
                  spaceBetween={16}
                  slidesPerView={1}
                  navigation
                  pagination={{ clickable: true }}
                  breakpoints={{
                    640: { slidesPerView: 1 },
                    768: { slidesPerView: 1 },
                  }}
                >
                  {profile.gallery_images.map((img, idx) => (
                    <SwiperSlide key={idx}>
                      <img
                        src={img}
                        alt={`Gallery ${idx}`}
                        className="mx-auto object-cover rounded-lg cursor-pointer"
                        onClick={() => {
                          setLightboxIndex(idx);
                          setLightboxOpen(true);
                        }}
                      />
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            ) : (
              <div className="mt-4 bg-secondary p-4 rounded-lg text-yellow-700">
                ⚠️ {lang === "id" ? "Tambahkan foto untuk menunjukkan aktivitas perusahaan!" : "Add photos to show company activity!"}
              </div>
            )}
          </div>

          {/* Lightbox */}
          <Lightbox
            open={lightboxOpen}
            index={lightboxIndex}
            close={() => setLightboxOpen(false)}
            slides={profile?.gallery_images?.map((src) => ({ src })) || []}
            plugins={[Zoom]}
          />


          {/* OFFICE ADDRESS */}
          <div className="bg-white p-6 rounded-xl shadow-sm">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">{lang === "id" ? "Alamat Kantor" : "Office Address"} <span className="text-red-500">*</span></h3>
              <button
                onClick={() => openModal("address")}
                className="flex items-center gap-1 text-primary"
              >
                <Pencil size={16} /> Edit
              </button>
            </div>

            <p className="mt-2 text-gray-700">
              {profile?.address || "No address added yet."}
            </p>


            <h3 className="text-lg font-semibold mt-2">{lang === "id" ? "Lokasi Kantor" : "Office Location"}</h3>

            {profile?.maps && (
              (() => {
                const decodedHtml = decodeHtml(profile.maps);

                // Ambil hanya iframe src
                const iframeSrc = extractIframeSrc(decodedHtml);

                return (
                  <>
                    {/* PREVIEW IFRAME CLEAN VERSION */}
                    {iframeSrc ? (
                      <iframe
                        src={iframeSrc}
                        width="100%"
                        height="300"
                        style={{ border: 0 }}
                        loading="lazy"
                        allowFullScreen
                        referrerPolicy="no-referrer-when-downgrade"
                        className="rounded-lg overflow-hidden border mt-4"
                      />
                    ) : (
                      <p className="text-red-500 mt-2">Invalid iframe embed</p>
                    )}
                  </>
                );
              })()
            )}
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-xl shadow-sm">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold text-lg">{lang === "id" ? "Kultur" : "Culture"} </h3>
              <button
                onClick={() => openModal("Culture")}
                className="flex items-center gap-1 text-primary"
              >
                <Pencil size={16} /> Edit
              </button>
            </div>
            <p className="mt-2 text-gray-700">{getLangValue(profile?.culture, lang) || "No culture description yet."}</p>
          </div>


          <div className="bg-white p-6 rounded-xl shadow-sm">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold text-lg">{lang === "id" ? "Manfaat" : "Benefits"} <span className="text-red-500">*</span></h3>
              <button
                onClick={() => openModal("Benefit")}
                className="flex items-center gap-1 text-primary"
              >
                <Pencil size={16} /> Edit
              </button>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {benefits.length === 0 ? (
                <p className="">
                  {lang === "id" ? "Belum ada manfaat" : "No benefit yet."}
                </p>
              ) : (
                benefits.map((b, i) => (
                  <span
                    key={i}
                    className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm"
                  >
                    {getBenefitLabel(b)}
                  </span>
                ))
              )}
            </div>

          </div>


          <div className="bg-white p-6 rounded-xl shadow-sm">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold text-lg">{lang === "id" ? "Media Sosial" : "Social Media"}</h3>
              <button
                onClick={() => openModal("Social Media")}
                className="flex items-center gap-1 text-primary"
              >
                <Pencil size={16} /> Edit
              </button>
            </div>

            <div className="mt-2 text-gray-700 break-all">
              {Object.values(socialMedia).some((v) => v) ? (
                Object.values(socialMedia)
                  .filter((v) => v)
                  .map((url, i) => (
                    <p className="mt-4" key={i}>{url}</p>
                  ))
              ) : (
                <p>No social media yet.</p>
              )}
            </div>

          </div>

        </div>
      </div>

      {/* MODALS */}
      <AdminModal
        open={modalType === "about"}
        onClose={closeModal}
        title="Edit About Us"
        isValid={isAboutValid}
        onSubmit={async () => {
          const result = await updateAboutUs({
            about_us: {
              id: draftAboutID,
              en: draftAboutEN,
            },
          });

          if (result.success) {
            toast.success(result.message || "About Us updated");
            await fetchCompanyProfile();
            closeModal()
          } else {
            toast.error(result.message || "Failed to update About Us");
          }
        }}


        tabs={{
          id: (
            <textarea
              className="w-full border rounded-lg p-3 h-40"
              value={draftAboutID}
              onChange={(e) => setDraftAboutID(e.target.value)}
            />
          ),
          en: (
            <textarea
              className="w-full border rounded-lg p-3 h-40"
              value={draftAboutEN}
              onChange={(e) => setDraftAboutEN(e.target.value)}
            />
          ),
        }}
      />


      {/* OTHER MODALS */}
      <AdminModal
        open={modalType === "gallery"}
        onClose={closeModal}
        title="Upload Gallery Photos"
        onSubmit={async () => {
          // Kirim hanya file baru
          const filesToUpload = galleryFiles
            .filter(f => f.isNew && f.file)
            .map(f => f.file!);

          if (filesToUpload.length === 0) {
            toast.error("Please select at least one image to upload");
            return;
          }

          const res = await updateGalleryImages(filesToUpload);

          if (res.success) {
            toast.success(res.message);

            // Ambil profile terbaru dari backend
            await fetchCompanyProfile();

            // Update galleryFiles: gabungkan file lama (dari profile baru) + file baru sudah di-upload
            setGalleryFiles([
              ...(res.data?.gallery_images || []).map(url => ({
                file: null,
                preview: url,
                isNew: false
              }))
            ]);

            closeModal();
          } else {
            toast.error(res.message);
          }
        }}
      >
        <FilePondUploader
          files={galleryFiles}
          onChange={setGalleryFiles}
          maxFiles={10}
          profileImages={profile?.gallery_images || []}
          modalOpen={modalType === "gallery"} // sync hanya saat modal dibuka
        />
      </AdminModal>




      <AdminModal
        open={modalType === "address"}
        onClose={closeModal}
        title="Office"
        isValid={isOfficeValid}
        onSubmit={async () => {
          const res = await updateAddress({
            address: officeData.address,
            maps: officeData.map,
          });

          if (res.success) {
            toast.success(res.message);
            await fetchCompanyProfile();
            closeModal();
          } else {
            toast.error(res.message);
          }
        }}
      >
        <OfficeAddressForm profile={profile} onChange={setOfficeData} />
      </AdminModal>

      <AdminModal
        open={modalType === "Culture"}
        isValid={isCultureValid}
        onClose={closeModal}
        title="Edit Culture"
        onSubmit={async () => {
          const result = await updateCulture({
            culture: {
              id: draftCultureID,
              en: draftCultureEN,
            },
          });

          if (result.success) {
            toast.success(result.message || "About Us updated");
            await fetchCompanyProfile();
            closeModal()
          } else {
            toast.error(result.message || "Failed to update About Us");
          }
        }}

        tabs={{
          id: (
            <textarea
              className="w-full border rounded-lg p-3 h-40"
              value={draftCultureID}
              onChange={(e) => setDraftCultureID(e.target.value)}
            />
          ),
          en: (
            <textarea
              className="w-full border rounded-lg p-3 h-40"
              value={draftCultureEN}
              onChange={(e) => setDraftCultureEN(e.target.value)}
            />
          )

        }}
      />

      <AdminModal
        open={modalType === "Benefit"}
        onClose={closeModal}
        title="Benefits"
        onSubmit={async () => {
          const benefitIds = benefits.map((id) => Number(id));

          const res = await updateBenefits(benefitIds);

          if (res.success) {
            toast.success(
              res.message || "Benefits updated successfully"
            );
            await fetchCompanyProfile();
            closeModal();
          } else {
            toast.error(res.message || "Failed to update benefits");
          }
        }}
      >
        <TagSelectInput
          value={benefits}
          onChange={setBenefits}
          options={benefitOptions}
        />
      </AdminModal>


      <AdminModal
        open={modalType === "Social Media"}
        onClose={closeModal}
        title="Social Media"
        onSubmit={async () => {
          const payload = { ...socialMedia };

          const res = await updateSocialMedia(payload);

          if (res.success) {
            toast.success(res.message || "Social media updated successfully");
            await fetchCompanyProfile();
            closeModal();
          } else {
            toast.error(res.message || "Failed to update social media");
          }
        }}
      >
        <SocialMediaForm value={socialMedia} onChange={setSocialMedia} />
      </AdminModal>
    </>
  );
}
